import { Alignment, Direction, Displaying, dontForwardTheseProps, Spacing } from "@bonusx-global/bx-design-system";
import styled from "@emotion/styled";
import Box from "@mui/material/Box";
import { useAtomValue } from "jotai";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import useIsMobile from "../../../../../hooks/device/useIsMobile";
import { useClientConstants } from "../../../../hooks/use-client-constants/use-client-constants";
import type { IWithIsMobile } from "../../../../interfaces/IWithIsMobile.interface";
import { GENERAL_SUBSCRIPTION_TO_BONUSX_SUBSCRIPTION_TIER_MAP } from "../../../../services/user-subscription/enums/bonusx-subscription-tiers.enum";
import Spacer from "../../../common/Spacer";
import SubscriptionPlanUpgradeButton from "../../../SubscriptionPlan/components/subscription-plan-upgrade-button";
import {
  UPGRADE_AND_CHAT_WITH_CLARA_TEXT,
  UPGRADE_AND_CHAT_WITH_CLARA_TEXT_KEY,
} from "../../../SubscriptionPlan/constants/button-label-keys.const";
import { ChatbotContext } from "../../chatbot.context";
import type { IChatbotMessage } from "../../entities/chatbot-message.entity";
import { SenderTypeEnum } from "../../enums/sender-type.enum";
import { chatbotOpenAtom } from "../../stores/chatbot.store";
import { AiChatbotIconContainer } from "../ai-chatbot-icon-container/ai-chatbot-icon-container";
import { AiChatbotIcon } from "../ai-chatbot-icon/ai-chatbot-icon";
import { ChatbotMessageItem } from "../messages/chatbot.message-item";
import {
  CHATBOT_UNAIVAILABLE_IN_ANY_TIER_DEFAULT_MESSAGE,
  CHATBOT_UNAVAILABLE_MESSAGE_CONTAINER_ID,
  CHATBOT_UNAVAILABLE_MESSAGE_DEFAULT_VALUE,
  CHATBOT_UNAVAILABLE_MESSAGE_KEY,
} from "./unavailable-chatbot-message.const";

export const UNAVAILABLE_CHATBOT_MESSAGE_DESKTOP_PADDING = `${Spacing["1500"]} ${Spacing["500"]} ${Spacing["750"]} ${Spacing["1500"]}`;
export const UNAVAILABLE_CHATBOT_MESSAGE_MOBILE_PADDING = `${Spacing["1500"]} ${Spacing["1000"]} 4px ${Spacing["1000"]}`;

const UnavailableChatbotMessageContainer = styled(Box, { shouldForwardProp: dontForwardTheseProps(["isMobile"]) })<IWithIsMobile>(({ isMobile }) => ({
  flexGrow: 1,
  display: Displaying.flex,
  flexDirection: Direction.column,
  justifyContent: Alignment.start,
  alignItems: Alignment.flexStart,
  padding: isMobile ? UNAVAILABLE_CHATBOT_MESSAGE_MOBILE_PADDING : UNAVAILABLE_CHATBOT_MESSAGE_DESKTOP_PADDING,
  overflow: isMobile ? "auto" : "hidden",
  scrollbarGutter: "stable",
  "&:hover": {
    overflowY: "auto",
  },
}));
interface UnavailableChatbotMessageProps {
  onClickUpgradeButton: () => void;
  requestableFromTier?: string;
}
function UnavailableChatbotMessage(props: Readonly<UnavailableChatbotMessageProps>): JSX.Element {
  const isMobile = useIsMobile();
  const { state } = useContext(ChatbotContext);
  const { getValueOrDefault } = useClientConstants();

  const isChatbotPanelOpen = useAtomValue(chatbotOpenAtom);
  const [canDisplayMessageWithAnimation, setCanDisplayMessageWithAnimation] = useState<boolean>(false);

  useEffect(
    function triggerMessageTypingAnimationOnlyTheFirstTime() {
      if (isChatbotPanelOpen && !canDisplayMessageWithAnimation) {
        setCanDisplayMessageWithAnimation(true);
      }
    },
    [isChatbotPanelOpen, canDisplayMessageWithAnimation]
  );

  const requiredTier = useMemo(
    function memoizeMinimumRequiredTier() {
      if (props.requestableFromTier) {
        return GENERAL_SUBSCRIPTION_TO_BONUSX_SUBSCRIPTION_TIER_MAP[props.requestableFromTier];
      }
      return undefined;
    },
    [props.requestableFromTier]
  );

  const messageText = useMemo(
    function memoizeMessageText() {
      if (!requiredTier) {
        return CHATBOT_UNAIVAILABLE_IN_ANY_TIER_DEFAULT_MESSAGE;
      }
      return getValueOrDefault(CHATBOT_UNAVAILABLE_MESSAGE_KEY, CHATBOT_UNAVAILABLE_MESSAGE_DEFAULT_VALUE);
    },
    [requiredTier]
  );

  const aiMessage: IChatbotMessage = {
    text: messageText,
    senderType: SenderTypeEnum.ASSISTANT,
    feedback: null,
    negativeFeedbackReason: null,
  };

  const showPaywallButton = useMemo(
    function memoizeShowPaywallButton() {
      return !state.isStreamingResponse && requiredTier;
    },
    [state.isStreamingResponse, requiredTier]
  );

  if (!canDisplayMessageWithAnimation) {
    return (
      <AiChatbotIconContainer>
        <AiChatbotIcon animated={true} />
      </AiChatbotIconContainer>
    );
  }

  return (
    <UnavailableChatbotMessageContainer isMobile={isMobile} id={CHATBOT_UNAVAILABLE_MESSAGE_CONTAINER_ID}>
      <ChatbotMessageItem message={aiMessage} animateAiMessage={true} />
      <Spacer spacingValue="1500" />

      {showPaywallButton ? (
        <SubscriptionPlanUpgradeButton
          labelKey={UPGRADE_AND_CHAT_WITH_CLARA_TEXT_KEY}
          labelFallback={UPGRADE_AND_CHAT_WITH_CLARA_TEXT}
          requestableFromTier={requiredTier}
          onClick={props.onClickUpgradeButton}
        />
      ) : (
        <></>
      )}
    </UnavailableChatbotMessageContainer>
  );
}

export default memo(UnavailableChatbotMessage);
